import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import { ReactComponent as BackIcon } from "../../assets/img/backArrow.svg";
import CustomInput from "../UIKits/CustomInput/CustomInput";
import { Body, Subtitle, Title } from "../UIKits/Typography/Typography";
import Button from "../UIKits/Button/Button";
import TextDetailContainer from "../TextDetailContainer/TextDetailContainer";
import TextDetail from "../TextDetail/TextDetail";
import { formatCurrency } from "../../helper/helper";
import { buyPowerSchema } from "../../schema/buy-power.schema";
import { PaymentMethod } from "../Utils/constants";
import { downloadInvoice, generateInvoice, getTransactionId, verifyAndFundWallet } from "../../services/selfService";
import { useFundWalletStore } from "../../store/fundWallet";
import { Download } from "lucide-react";
import { remitaPaymentService } from "../../services/payment";
import { downloadInvoiceFile } from "../../helper/downloadHelper";
import { usePowerPurchaseStore } from "../../store/buyPowerStore";

export const AmountStep = () => {
  const { setAmount, amount, meterNumber, setInvoiceData,    nextStep,  } =
    useFundWalletStore();

  const [loading, setLoading] = useState(false);
   

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      amount: amount || null,
      meterNumber: meterNumber || null,
    },
    resolver: yupResolver(buyPowerSchema),
  });
  const { triggerRefetch } = usePowerPurchaseStore();
  const watchedAmount = watch("amount");
  const amountOptions = [5000, 10000, 20000, 25000, 40000, 50000];

  const handleProceedToSummary = async (data: any) => {
    setLoading(true);
    try {
      const response = await generateInvoice({
        amount: Number(data.amount),
        isWalletFunding: true,
      });
      if (response?.data?.isSuccessful && response?.data?.data) {
        const invoiceData = response.data;

        setInvoiceData(invoiceData.data);
        setAmount(Number(data.amount));
        triggerRefetch();
        nextStep();
      }else {
        toast.error(response?.data?.message || "Failed to generate invoice. Please try again.");
      }
    } catch (error: any) {
       

      const errorMessage =
        error.response?.data?.message || error.response?.data?.errors?.[0] || error.message || "An error occurred";
      toast.error(errorMessage || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

   

  return (
    <div className="space-y-6">
      <Title level={3}>Fund wallet</Title>
      <Subtitle>How much would you like to fund? Note that funds deposited can not be withdrawn</Subtitle>

      <div className="space-y-3.5">
        <CustomInput
          placeholder="Enter or select amount"
          name="amount"
          control={control}
          label="Amount"
          type="number"
          errorMessage={errors.amount?.message}
        />

        {/* Quick Amount Selection */}
        <div className="flex justify-between items-center flex-wrap gap-2">
          {amountOptions.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setValue("amount", option)}
              className={`text-xs font-semibold px-3.5 py-2 bg-[#F9F9F9] hover:bg-primary-50 hover:text-primary-600 transition-all duration-200 rounded-full ${
                Number(watchedAmount) === option
                  ? "bg-primary-100 text-primary-600 border border-primary-500"
                  : "text-foreground"
              }`}
            >
              {formatCurrency(option, 0)}
            </button>
          ))}
        </div>
      </div>

      {/* Proceed Button */}
      <div className="pt-8">
        <Button
          className="!w-full "
          onClick={handleSubmit(handleProceedToSummary)}
          disabled={loading}
          loading={loading}
        >
          {" "}
          Continue
        </Button>
      </div>
    </div>
  );
};

//SummaryStep.tsx
interface InvoiceSummaryStepProps {
  onClose: () => void;
  onPaymentSuccess: () => void;
  onPaymentError: () => void;
}
export const SummaryStep: React.FC<InvoiceSummaryStepProps> = ({ onPaymentSuccess, onPaymentError, onClose }) => {
  const { amount, meterDetails, invoiceData,  setPaymentResult, previousStep, setLoading } =
    useFundWalletStore();
  const { triggerRefetch } = usePowerPurchaseStore();

  const [downloadingInvoice, setDownloadingInvoice] = useState(false);

  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const handleDownloadInvoice = async (invoiceNumber: string) => {
    setDownloadingInvoice(true);

    try {
      const response = await downloadInvoice(invoiceNumber);

      if (response.data) {
        // This handles both base64 and URL automatically!
        downloadInvoiceFile(response.data, invoiceNumber, "invoice");

        toast.success("Invoice downloaded successfully");
      }
    } catch (error: any) {
      toast.error(error.data?.message || "Failed to download invoice");
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const handlePayOnline = async () => {   
    if (!invoiceData) return;

    setIsProcessingPayment(true);
    setLoading(true);
    onClose();

    try {
      let payload = {
        transactionId: invoiceData.transactionId as string,
        amount: invoiceData.amount,
        meterNo: meterDetails.meterNumber,
        paymentMethod: PaymentMethod.Remita,
        invoiceNumber: invoiceData.invoiceNumber,
        remitaPaymentResponse: "",
      };

      const transactionIdResponse = await getTransactionId({
        invoiceNumber: invoiceData.invoiceNumber,
        transactionId: invoiceData.transactionId as string,
      });
      if (!transactionIdResponse?.data?.isSuccessful || !transactionIdResponse?.data?.data) {
        toast.error("Failed to generate transaction ID. Please try again.");
        setIsProcessingPayment(false);
        setLoading(false);
        return;
      }

      const transactionId = transactionIdResponse.data.data;
      payload.transactionId = transactionId;

      // Initialize Remita payment

      await remitaPaymentService.initializePayment(
        {
          key: "", // From env
          rrr: invoiceData.rrr,
          transactionId,
          amount,
        },
        {
          onSuccess: async (remitaResponse) => {
 
            const remitaResponseData = remitaResponse ? JSON.stringify(remitaResponse) : "anything";
            // Verify payment on backend AND call verify fund wallet
            payload.remitaPaymentResponse = remitaResponseData;
            const verificationResponse = await verifyAndFundWallet({
              transactionId: transactionId as string,
              amount: invoiceData.amount,
              invoiceNumber: invoiceData.invoiceNumber,
              // remitaPaymentResponse: remitaResponseData,
            });

            if (verificationResponse.data.isSuccessful && verificationResponse.data.data) {
               
              const verificationResponseData = verificationResponse.data.data;
              setPaymentResult({
                amount: verificationResponseData.amount || invoiceData.amount,
              });

              onPaymentSuccess();
               
              toast.success("Payment successful!");
            }
            triggerRefetch();
          },
          onError: async (remitaResponse) => {
            toast.error(remitaResponse.message || "Payment failed");
            setIsProcessingPayment(false);
            const remitaResponseData = remitaResponse ? JSON.stringify(remitaResponse) : "anything";
            // Verify payment on backend
            payload.remitaPaymentResponse = remitaResponseData;
            const verificationResponse = await verifyAndFundWallet({
              transactionId: invoiceData.transactionId as string,
              amount: invoiceData.amount,
              invoiceNumber: invoiceData.invoiceNumber,
              // remitaPaymentResponse: remitaResponseData,
            });

            if (verificationResponse.data.isSuccessful && verificationResponse.data.data) {
 
              onPaymentError();
            }
            triggerRefetch();
          },

          onClose: () => {
            // setIsProcessingPayment(false);
            // setLoading(false);
          },
        }
      );
    } catch (error: any) {
      const errorMessage = error.data?.message || error.message;
      toast.error(errorMessage || "Failed to initialize payment");
    } finally {
      setIsProcessingPayment(false);
      setLoading(false);
    }
  };

  if (!invoiceData) return null;

  return (
    <div className="space-y-6">
      {/* Back Button */}

      <button
        className=" absolute top-9 left-0 p-1 bg-white hover:bg-ash transition-all duration-300 ease-in rounded-full"
        onClick={previousStep}
      >
        <BackIcon />
      </button>

      <Title level={3}>Summary</Title>

      {/* Meter Details */}
      <div className="space-y-3">
        <Body color="tertiary">Details</Body>
        <TextDetailContainer className="!text-xs">
          <TextDetail title="Invoice number" value={invoiceData?.invoiceNumber} />
          <TextDetail
            title="RRR"
            value={invoiceData?.rrr}
            onClick={() => copyToClipboard(invoiceData?.rrr || "", "RRR")}
          />

          <TextDetail title="Amount to fund" value={formatCurrency(invoiceData?.amount || amount)} />
        </TextDetailContainer>
      </div>

      {/* Generate Invoice Button */}

      <div className="flex  gap-4 mt-8">
        <Button
          variant="outline"
          className="!w-full"
          disabled={downloadingInvoice}
          onClick={() => handleDownloadInvoice(invoiceData.invoiceNumber)}
        >
          <div className="flex items-center justify-center">
            <Download className=" mr-2 w-4 h-4" />

            <p> {downloadingInvoice ? "Downloading..." : "Download Invoice"}</p>
          </div>
        </Button>
        <Button
          className="!w-full "
          loading={isProcessingPayment}
          onClick={handlePayOnline}
          disabled={isProcessingPayment}
        >
          Pay online
        </Button>
      </div>
    </div>
  );
};
